// SubscriptionContext — v0.8.0 (Stripe + Subscription Tiers)
//                      v1.2.0 — three-tier expansion (Investor / Professional / Enterprise)
//
// Single source of truth for the signed-in user's subscription state.
// Reads the public.profiles row from Supabase and exposes derived booleans
// so every component can gate features without duplicating date math.
//
// Exposed values:
//   profile         — raw profiles row (null while loading)
//   tier            — 'free' | 'investor' | 'professional' | 'enterprise'
//   status          — 'trialing' | 'active' | 'canceled' | 'past_due' | 'free'
//   trialDaysLeft   — integer days remaining in trial (0 when expired)
//   isTrialing      — status === 'trialing' AND trialDaysLeft > 0
//   isInvestor      — status === 'active' AND tier === 'investor'
//   isProfessional  — status === 'active' AND tier === 'professional'
//   isEnterprise    — status === 'active' AND tier === 'enterprise'
//   isPro           — true for any paid active tier. Preserved for
//                     PaywallGate/PaywallBanner/Pricing backward
//                     compatibility. Prefer isInvestor/isProfessional/
//                     isEnterprise for tier-specific feature gates.
//   isExpired       — trial has ended AND not an active subscriber
//   loading         — true while the first fetch is in flight
//
// Helper functions (called on button clicks):
//   startCheckout(priceId?)  — POST to stripe-checkout → redirect to Stripe
//   openPortal()             — POST to stripe-portal  → redirect to Stripe

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../services/api';
import { getEntitlementContext, TIERS as ENTITLEMENT_TIERS } from '../lib/entitlements';
import { deriveCreditState, fetchTrialCreditUsage } from '../lib/analysisCredits';

// Canonical subscription tier strings. As of BL-041 these are re-exported
// from the single entitlement catalog (frontend/src/lib/entitlements.js) so
// there is ONE canonical tier definition shared with the backend engine —
// never hardcode the strings. Frozen so a typo at a call site throws.
export const TIERS = ENTITLEMENT_TIERS;

// Pure derivation of subscription flags from a profiles row. As of BL-041
// this delegates to the consolidated entitlement engine
// (getEntitlementContext) and projects the subset of flags the paywall
// surface (PaywallGate, PaywallBanner, Pricing, DevSubscriptionPanel)
// consumes. The flag shape is byte-for-byte preserved; the underlying math
// now lives in the single evaluation surface shared with the backend.
//
// `profile` — raw row from `supabase.from('profiles').select('*').eq(...).single()`,
//   or null/undefined when the user is signed out or pre-Migration-017.
// `now` — current time in ms (Date.now() by default). Override in tests
//   for deterministic trialDaysLeft computation.
export function deriveSubscriptionFlags(profile, now = Date.now()) {
  const ctx = getEntitlementContext(profile, { now });
  return {
    tier:           ctx.tier,
    status:         ctx.status,
    trialDaysLeft:  ctx.trialDaysLeft,
    isTrialing:     ctx.isTrialing,
    isInvestor:     ctx.isInvestor,
    isProfessional: ctx.isProfessional,
    isEnterprise:   ctx.isEnterprise,
    isPro:          ctx.isPro,
    isExpired:      ctx.isExpired,
  };
}

const SubscriptionContext = createContext(null);

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// Shared helper to call a Stripe Netlify function with the user's Bearer token.
async function callStripeApi(path, body = {}) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('Not signed in.');

  const res = await fetch(`${API_BASE_URL}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

export function SubscriptionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  // BL-040 — consumed trial analyses (advisory; from the analysis_credit_events
  // ledger via RLS SELECT-own-rows). 0 for paid/free; only meaningful trialing.
  const [creditsUsed, setCreditsUsed] = useState(0);
  // PLATFORM-002 — bumping this forces a profile re-fetch (used after the user
  // accepts the Terms of Service so the acceptance gate re-evaluates).
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshProfile = useCallback(() => setRefreshTick((t) => t + 1), []);

  // Fetch the profiles row whenever the signed-in user changes.
  //
  // Gate on AuthContext's initial loading state. AuthContext fires
  // supabase.auth.getSession() at mount, which holds the GoTrue lock
  // until it resolves. In the same tick the onAuthStateChange listener
  // fires INITIAL_SESSION synchronously, populating `user` before
  // getSession() returns. If we fire the profile query here off that
  // early `user` value, supabase-js tries to validate/refresh the token
  // for our PostgREST call and races the still-in-flight getSession()
  // for the lock — surfacing as "Lock was stolen by another request".
  // Waiting for authLoading=false guarantees getSession() has released
  // the lock before we run our own supabase.* call.
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchProfile() {
      setLoading(true);
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!cancelled) {
        // If no DB row yet (existing user pre-dating Migration 017), return a
        // synthetic trialing profile in memory. The row gets created the first
        // time the DevPanel or mock checkout calls mock-stripe-confirm/reset,
        // which both use upsert. We never write from the client here to avoid
        // the StrictMode double-effect lock conflict.
        const resolved = data || {
          id:                  user.id,
          subscription_status: 'trialing',
          subscription_tier:   'free',
          trial_ends_at:       new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          current_period_end:  null,
          stripe_customer_id:  null,
        };
        setProfile(resolved);
        setLoading(false);
      }

      // BL-040 — advisory trial-credit usage. Only fetch when the resolved
      // state is metered (trialing); paid/free never consume credits. Fails
      // soft to 0 (never blocks the UI — the backend is the real boundary).
      const resolvedCtx = getEntitlementContext(data || {
        subscription_status: 'trialing', subscription_tier: 'free',
        trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      });
      if (resolvedCtx.limits?.analysis_volume != null) {
        const used = await fetchTrialCreditUsage(supabase, user.id);
        if (!cancelled) setCreditsUsed(used);
      } else if (!cancelled) {
        setCreditsUsed(0);
      }
    }

    fetchProfile();
    return () => { cancelled = true; };
  }, [user, authLoading, refreshTick]);

  // ── Derived state ───────────────────────────────────────────────────────
  // All flag math lives in the consolidated entitlement engine (BL-041).
  // deriveSubscriptionFlags() projects the paywall flag subset; the full
  // context additionally carries advisory `capabilities` + `limits` for
  // feature-aware UX (display only — the backend is the security boundary).
  const flags = deriveSubscriptionFlags(profile);
  const { capabilities, limits } = getEntitlementContext(profile);
  // BL-040 — advisory trial-credit state derived from the resolved limit +
  // consumed count. Drives PaywallBanner + the analyzer exhaustion UX.
  const creditState = deriveCreditState(limits, creditsUsed);

  // ── Stripe helpers ───────────────────────────────────────────────────────

  const startCheckout = useCallback(async (priceId) => {
    const data = await callStripeApi('stripe-checkout', priceId ? { priceId } : {});
    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error(data?.error || 'Could not start checkout.');
    }
  }, []);

  const openPortal = useCallback(async () => {
    const data = await callStripeApi('stripe-portal', {
      returnUrl: window.location.origin + '/account',
    });
    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error(data?.error || 'Could not open billing portal.');
    }
  }, []);

  const value = {
    profile,
    loading,
    refreshProfile,   // PLATFORM-002 — re-fetch profile (post ToS acceptance)
    ...flags,
    capabilities,   // advisory: { deal_analyzer, lease_leverage, ... } — UX only
    limits,         // advisory: { analysis_volume } — UX only
    // BL-040 advisory trial-credit fields (UX only — backend enforces):
    analysisCreditsLimit:     creditState.limit,      // null when unmetered (paid)
    analysisCreditsUsed:      creditState.used,
    analysisCreditsRemaining: creditState.remaining,  // null when unmetered
    analysisCreditsExhausted: creditState.exhausted,
    analysisCreditsLow:       creditState.lowCredits,
    startCheckout,
    openPortal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
